import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { Box, Grid } from '@mui/material'
import WaysToEarnPoints from '../../../../components/leaderboard/WaysToEarnPoints'
import InstagramEngagementBonusCard from '../../../../components/InstagramEngagementBonusCard'
import FAQs from '../../about/components/FAQs'
import MonthlyCreditHero from './MonthlyCreditHero'
import {
  IWayToEarn,
} from '../../../../types/leaderboardTypes'
import PlainModal from '../../../../components/modal/PlainModal'
import HowToEarnPoints from './model/HowToEarnPoints'
import { ROUTES } from '../../../../helpers/routes-helper'
import { openInstagram } from '../../../../helpers/instagram-helper'
import useAppUtilities from '../../../../hooks/useAppUtilities'
import BasicModal from '../../../../components/modal/BasicModal'
import UploadReceipt from './model/UploadReceipt'
import CoinsReceived from './model/CoinsReceived'
import Newsletter from './model/Newsletter'
import PromoModal from '../../../../components/PromoModal'
import ShareInvite from '../../../../components/ShareInvite'
import { useAppSelector } from '../../../../store'
import {
  awardInstagramPoints,
  fetchEater,
  selectEater,
  selectShouldOpen,
  selectMessage,
  selectSeverity,
  setOpen,
} from '../../../../store/profileSlice'
import { selectEaterIsSubscribed } from '../../../../store/authSlice'
import { MuiAlert, MuiSnackbar } from '../../styled/global.styled'
import { debugLog, errorLog } from '../../../../utils/log-helper'
import { buildEarnWays } from '../../../../helpers/rewardsEarnWays'
import { fetchWalletBalance, WalletBalanceResponse } from '../../../../services/rewardsService'
import { mapSubscriptionPlanIdToTier } from '../../../../helpers/tier-helper'
import { getTierConfig } from '../../../../config/subscription-tiers'
import { POINTS_FAQ_DATA } from '../../../../constants/pointsFaq'

const EaterLeaderboard: React.FC = () => {
  const { navigate, dispatch } = useAppUtilities()
  const [howTOEarnPoints, setHowToEarnPoints] = useState(false)
  const [uploadReceipt, setUploadReceipt] = useState(false)
  const [coinsReceived, setCoinsReceived] = useState(false)
  const [newsletter, setNewsletter] = useState(false)
  const [isPromoModalOpen, setIsPromoModalOpen] = useState(false)
  const [shareInvite, setShareInvite] = useState(false)

  const eater = useAppSelector(selectEater)
  const profileShouldOpen = useAppSelector(selectShouldOpen)
  const profileMessage = useAppSelector(selectMessage)
  const profileSeverity = useAppSelector(selectSeverity)

  // Live wallet balance drives the monthly-credit hero (this month's qualifier progress) and its
  // secondary lifetime-redeemable line. Fetched once here (not per-card) so the points page keeps a
  // single /wallet/balance call — see TODO(wallet-balance-dedup) in rewardsService.
  const [walletBalance, setWalletBalance] = useState<WalletBalanceResponse | null>(null)
  const isSubscribed = useAppSelector(selectEaterIsSubscribed)

  // Exposed as a callback so a points-earning action on this page (e.g. the Instagram engagement
  // bonus) can refresh the hero in place, without a navigate-away/return. Safe to call repeatedly.
  const refreshBalance = useCallback(async (): Promise<void> => {
    try {
      const balance = await fetchWalletBalance()
      setWalletBalance(balance)
    } catch (error) {
      // Leave the hero on its last-known / empty state if the balance fetch fails.
      setWalletBalance(null)
    }
  }, [])

  useEffect(() => {
    void refreshBalance()
  }, [refreshBalance])

  // Load the full eater WITH earn ways + earned status on mount. fetchEater is the only call that
  // requests includeEarnWays=true; every other eater fetch (session/login) omits it. Without this,
  // landing on the points page directly leaves eater.earnWays empty, so buildEarnWays falls back to
  // the hardcoded default list — wrong "coming soon" states (e.g. Upload receipt shown as active)
  // and no earned-green highlight. Fetching here makes the list correct regardless of entry path
  // or platform. Reducer merges (preserves heavy collections), so this is safe to run on mount.
  useEffect(() => {
    void dispatch(fetchEater())
  }, [dispatch])

  // Resolve the current tier (mirrors EaterSubscriptionPage) so the hero has a
  // goal + rollover fallback when the backend omits monthlyGoalPoints.
  const currentPlan =
    !isSubscribed || !eater?.subscribed || !eater?.subscriptionPlanId
      ? null
      : mapSubscriptionPlanIdToTier(eater.subscriptionPlanId)
  const currentTier = getTierConfig(currentPlan)

  // Build earn ways list from eaterDTO using centralized adapter
  // Adapter now uses backend earnWays and earnWayStatus fields
  const waysToEarn: IWayToEarn[] = useMemo(() => {
    const earnWays = buildEarnWays(eater)

    // Dev-only verification logging
    if (process.env.NODE_ENV !== 'production') {
      debugLog('[EaterLeaderboard] Earn ways verification:', {
        earnWaysCount: eater?.earnWays?.length ?? 0,
        earnWayStatusKeys: eater?.earnWayStatus ? Object.keys(eater.earnWayStatus) : [],
        earnRulesMenuScanNew: eater?.earnRules?.menuScanNewPoints,
        earnRulesMenuScanRepeat: eater?.earnRules?.menuScanRepeatPoints,
        builtEarnWaysCount: earnWays.length,
      })
    }

    // Convert EarnWayRow to IWayToEarn format for compatibility
    return earnWays.map((way) => ({
      id: way.key,
      title: way.title,
      action: way.ctaText,
      status: way.status,
      earned: way.status === 'earned' || way.status === 'claimed', // Backward compatibility
      onClickAction: way.onClickAction,
    }))
  }, [eater])

  // const LeaderboardRankingList: ILeaderboardRanking[] = [
  //   { rank: 1, name: 'Alice Smith', points: 4000 },
  //   { rank: 2, name: 'Bob Johnson', points: 3900 },
  //   { rank: 3, name: 'Charlie Brown', points: 3850 },
  //   { rank: 4, name: 'Diana Prince', points: 3800 },
  //   { rank: 5, name: 'Ethan Hunt', points: 3750 },
  //   { rank: 6, name: 'Fiona Gallagher', points: 3700 },
  //   { rank: 7, name: 'George Washington', points: 3650 },
  //   { rank: 8, name: 'Hannah Montana', points: 3500 },
  //   { rank: 9, name: 'Ian Malcolm', points: 3200, me: true },
  //   { rank: 10, name: 'Jessica Jones', points: 2800 },
  //   { rank: 11, name: 'Kevin Hart', points: 2400 },
  //   { rank: 12, name: 'Peter Markson', points: 2200 },
  //   { rank: 13, name: 'Mike Wazowski', points: 2150 },
  //   { rank: 14, name: 'Nina Simone', points: 2100 },
  //   { rank: 15, name: 'Oscar Isaac', points: 1900 },
  //   { rank: 16, name: 'Paula Patton', points: 1800 },
  //   { rank: 17, name: 'Quentin Tarantino', points: 1780 },
  //   { rank: 18, name: 'Rita Moreno', points: 1650 },
  //   { rank: 19, name: 'Nina Simone', points: 1600 },
  //   { rank: 20, name: 'Kevin Hart', points: 1500 },
  // ]

  // Points earning info is now backend-driven via HowToEarnPoints component
  // Legacy points array removed - component uses selectEarnConfig

  const onEarnPoint = async (item: IWayToEarn): Promise<void> => {
    // Use onClickAction if available, otherwise fall back to id
    const actionId = (item as any).onClickAction || item.id

    switch (actionId) {
      case 'how-to-earn-points':
        setHowToEarnPoints(true)
        break
      case 'refer-and-earn':
        navigate(ROUTES.REFER_AND_EARN)
        break
      case 'upload-receipt':
        setUploadReceipt(true)
        break
      case 'get-others-join-via-groups':
        navigate(ROUTES.SOCIAL)
        break
      case 'login-and-use-app':
        setIsPromoModalOpen(true)
        break
      case 'subscribe-to-newsletter':
        setNewsletter(true)
        break
      case 'invite-a-restaurant':
        setShareInvite(true)
        break
      case 'follow-us-on-instagram':
        // Check current status from eaterDTO (may have changed)
        // eslint-disable-next-line no-case-declarations
        const currentInstagramBonusClaimed = eater?.instagramBonusClaimed === true || eater?.hasClaimedInstagramBonus === true
        if (!currentInstagramBonusClaimed) {
          try {
            const result = await dispatch(awardInstagramPoints())
            if (awardInstagramPoints.fulfilled.match(result)) {
              // Refetch eater to get updated instagramBonusClaimed status
              await dispatch(fetchEater())
              // Open Instagram URL
              await openInstagram()
            } else if (awardInstagramPoints.rejected.match(result)) {
              const payload = result.payload as { message?: string; alreadyClaimed?: boolean } | undefined
              if (payload?.alreadyClaimed) {
                // Already claimed error - refetch to update state
                await dispatch(fetchEater())
              }
              // Still open Instagram even if already claimed
              await openInstagram()
            }
          } catch (error) {
            // Error handling is done in the thunk
            errorLog('Error claiming Instagram bonus:', error)
          }
        } else {
          // Already claimed - just open Instagram
          await openInstagram()
        }
        break
      default:
        break
    }
  }

  const handleRedeemPoints = (): void => {
    // For eaters, we don't have subscription gating yet
    navigate(ROUTES.REDEEM_POINTS)
  }

  const handleCloseProfileSnackbar = (): void => {
    dispatch(setOpen(false))
  }

  return (
    <Box my={{ xs: 2, md: 5 }}>
      <Grid container spacing={6}>
        <Grid item xs={12} md={6}>
          <Box mb={2} sx={{ maxWidth: '100%' }}>
            <InstagramEngagementBonusCard onPointsAwarded={refreshBalance} />
          </Box>
          {/* Hero: this month's credit qualifier is the primary number; the monthly panel is now
              consolidated here so the monthly progress isn't shown twice in conflicting ways. */}
          <MonthlyCreditHero
            wallet={walletBalance}
            tier={currentTier}
            onRedeem={handleRedeemPoints}
          />
        </Grid>
        <Grid item xs={12} md={6} sx={{ height: { xs: 'auto', md: '100%' } }}>
          <WaysToEarnPoints
            wayToEarnList={waysToEarn}
            onWayClick={(item) => {
              void onEarnPoint(item)
            }}
          />
        </Grid>
      </Grid>
      <FAQs
        faqData={POINTS_FAQ_DATA}
        title="Frequently Asked Questions"
        my={6}
        titleTypography="h3"
      />
      <PlainModal
        handleClose={() => setHowToEarnPoints(false)}
        open={howTOEarnPoints}
        title="How to earn points"
      >
        <HowToEarnPoints />
      </PlainModal>
      <BasicModal
        onCancel={() => setUploadReceipt(false)}
        onConfirm={() => {
          setUploadReceipt(false)
          setCoinsReceived(true)
        }}
        handleClose={() => setUploadReceipt(false)}
        open={uploadReceipt}
        title="Upload Receipt"
        cancelButtonName="Cancel"
        buttonName="Upload"
        modelWidth="480px"
      >
        <UploadReceipt />
      </BasicModal>
      <CoinsReceived
        open={coinsReceived}
        handleClose={() => setCoinsReceived(false)}
        actionButton={() => setCoinsReceived(false)}
        title="24 Coins Received"
        subTitle="Lorem ipsum dolor sit amet consectetur. Et tellus interdum nunc malesuada."
        actionBUttonText="Check your points"
      />
      <Newsletter
        open={newsletter}
        handleClose={() => setNewsletter(false)}
        actionButton={() => setNewsletter(false)}
      />
      <ShareInvite
        open={shareInvite}
        handleClose={() => setShareInvite(false)}
        url=""
      />
      {isPromoModalOpen && (
        <PromoModal onClose={() => setIsPromoModalOpen(false)} type="mobile" />
      )}
      <MuiSnackbar
        open={profileShouldOpen}
        onClose={handleCloseProfileSnackbar}
        autoHideDuration={5000}
      >
        <MuiAlert
          onClose={handleCloseProfileSnackbar}
          severity={profileSeverity ?? 'success'}
        >
          {profileMessage}
        </MuiAlert>
      </MuiSnackbar>
    </Box>
  )
}

export default EaterLeaderboard
